'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'
import { Button } from '@/components/button'
import { SlidingPane } from '@/components/sliding-pane'
import { T } from '@/components/t'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailMenu } from '@/features/restaurants/restaurant-detail/restaurant-detail-menu/restaurant-detail-menu'
import { Locale } from '@/setup/locales'
import { getShareableUrl, UrlConfig } from '@/setup/routing'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { buildRestaurantDeeplink } from '@/utils/url/deeplinks'

interface IRestaurantDetailMobileCtaProps {
    className?: string
}

export const RestaurantDetailMobileCta = ({ className }: IRestaurantDetailMobileCtaProps) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const { citySlug, restaurantSlug } = useRestaurantDetailContext()

    const _t = useTranslations()

    const locale = useLocale()

    const onShareClick = useCallback(() => {
        if (navigator.share) {
            const currentOgTitle = document
                .querySelector('meta[property="og:title"]')
                ?.getAttribute('content')

            const currentTitle = document.title

            const restaurantUrl = UrlConfig['/restaurants/[citySlug]/[restaurantSlug]'].getUrl({
                citySlug,
                restaurantSlug,
            })

            const shareableUrl = getShareableUrl(restaurantUrl, locale as Locale)

            navigator
                .share({
                    title: currentOgTitle ?? currentTitle ?? 'NeoTaste',
                    url: shareableUrl,
                })
                .catch((e) => {
                    // Navigatior.share sees user canceling as an error
                    // biome-ignore lint/suspicious/noConsole: intentional error logging
                    console.log('Did not share', e)
                })
        }
    }, [citySlug, restaurantSlug, locale])

    const restaurantDeeplink = buildRestaurantDeeplink(restaurantSlug)

    return (
        <div
            className={cn('md:hidden flex w-full gap-2 overflow-x-auto scrollbar-hide', className)}
        >
            <a href={restaurantDeeplink} className="shrink-0">
                <Button
                    styleType="tertiary"
                    shape="pill"
                    borderColor={COLORS.grey[300]}
                    className="border-[2px] whitespace-nowrap"
                    onClick={() => {}}
                >
                    <Image
                        src="/_images/share/neotaste-icon-logo.png"
                        alt="NeoTaste"
                        width={16}
                        height={16}
                        className="shrink-0 rounded-s"
                    />
                    <T t="restaurants.detail.cta.openInApp" />
                </Button>
            </a>

            <Button
                onClick={onShareClick}
                styleType="tertiary"
                shape="pill"
                borderColor={COLORS.grey[300]}
                className="border-[2px] whitespace-nowrap shrink-0"
            >
                <T t="restaurants.detail.cta.share" />
            </Button>

            <Button
                onClick={() => setIsMenuOpen(true)}
                styleType="tertiary"
                shape="pill"
                borderColor={COLORS.grey[300]}
                className="border-[2px] whitespace-nowrap shrink-0"
            >
                <T t="restaurants.detail.cta.menu" />
            </Button>

            <SlidingPane onClose={() => setIsMenuOpen(false)} isOpen={isMenuOpen}>
                <div className="w-full p-4">
                    <RestaurantDetailMenu />
                </div>
            </SlidingPane>
        </div>
    )
}
